/**
 * AgentBuilder Run Store
 *
 * Tracks active agent builder runs and per-node status updates.
 */

import { type GetStoreState, type SetStoreState, createStore } from '@seeqdev/utils';

import type { NodeStatus } from '@/agentBuilder/agentBuilder.types';

interface NodeStatusUpdate {
  status: NodeStatus;
  timestamp: number;
}

export interface ActiveAgentBuilderRun {
  runId: string;
  agentBuilderId: string;
  nodeStatuses: Record<string, NodeStatusUpdate>;
  isRunning: boolean;
  endedWithError?: boolean;
  endedCanceled?: boolean;
}

export interface AgentBuilderRunState {
  activeRuns: Record<string, ActiveAgentBuilderRun>;
}

interface AgentBuilderRunActions {
  runStarted: (runId: string, agentBuilderId: string) => void;
  runNodeStatus: (agentBuilderId: string, nodeId: string, status: NodeStatus) => void;
  runCompleted: (agentBuilderId: string, error?: boolean) => void;
  runCanceled: (agentBuilderId: string) => void;
  runCleared: (agentBuilderId: string) => void;
}

const initialState: AgentBuilderRunState = {
  activeRuns: {},
};

const agentBuilderRunActionsFactory = (
  set: SetStoreState<AgentBuilderRunState>,
  _get: GetStoreState<AgentBuilderRunState>,
) => ({
  runStarted: (runId: string, agentBuilderId: string) =>
    set((draft: AgentBuilderRunState) => {
      draft.activeRuns[agentBuilderId] = {
        runId,
        agentBuilderId,
        nodeStatuses: {},
        isRunning: true,
      };
    }),

  runNodeStatus: (agentBuilderId: string, nodeId: string, status: NodeStatus) =>
    set((draft: AgentBuilderRunState) => {
      const run = draft.activeRuns[agentBuilderId];
      if (!run) return;
      run.nodeStatuses[nodeId] = { status, timestamp: Date.now() };
    }),

  runCompleted: (agentBuilderId: string, error?: boolean) =>
    set((draft: AgentBuilderRunState) => {
      const run = draft.activeRuns[agentBuilderId];
      if (!run) return;
      run.isRunning = false;
      run.endedWithError = error;
    }),

  runCanceled: (agentBuilderId: string) =>
    set((draft: AgentBuilderRunState) => {
      const run = draft.activeRuns[agentBuilderId];
      if (!run) return;
      run.isRunning = false;
      run.endedCanceled = true;
      run.endedWithError = false;
    }),

  runCleared: (agentBuilderId: string) =>
    set((draft: AgentBuilderRunState) => {
      delete draft.activeRuns[agentBuilderId];
    }),
});

const [AgentBuilderRunStoreProvider, useAgentBuilderRunStoreInstance, useAgentBuilderRunSelector] = createStore<
  AgentBuilderRunState,
  AgentBuilderRunActions
>('AgentBuilderRun', initialState, agentBuilderRunActionsFactory);

export { AgentBuilderRunStoreProvider };

/**
 * Get agent builder run actions (NEVER causes re-renders)
 */
export const useAgentBuilderRunActions = () => useAgentBuilderRunStoreInstance().actions;

/**
 * Get active runs map
 */
export const useActiveAgentBuilderRuns = () => useAgentBuilderRunSelector((state) => state.activeRuns);

/**
 * Get active run by agent builder id
 */
export const useActiveAgentBuilderRun = (agentBuilderId?: string) =>
  useAgentBuilderRunSelector((state) => (agentBuilderId ? state.activeRuns[agentBuilderId] : undefined));
